<template>
  <v-container fluid>
    <v-card class="mb-4">
      <v-card-title>
        Reporte de Ventas por Rango
      </v-card-title>
      <v-card-text>
        <v-row dense>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="filters.dateFrom"
              type="date"
              label="Fecha inicial"
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="filters.dateTo"
              type="date"
              label="Fecha final"
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="filters.groupBy"
              :items="groupOptions"
              item-text="text"
              item-value="value"
              label="Agrupar por"
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="filters.sellerId"
              :items="sellers"
              item-text="name"
              item-value="id"
              label="Vendedor"
              clearable
              outlined
              dense
            />
          </v-col>
        </v-row>

        <div class="d-flex flex-wrap align-center mt-2">
          <v-btn color="primary" class="mr-2 mb-2" :loading="loading" @click="runReport">
            <v-icon left>mdi-play</v-icon>
            Ejecutar
          </v-btn>
          <v-btn text class="mr-2 mb-2" @click="clearFilters">
            <v-icon left>mdi-filter-remove-outline</v-icon>
            Limpiar filtros
          </v-btn>
          <v-btn color="success" class="mb-2" :loading="downloading" :disabled="rows.length === 0" @click="downloadReport">
            <v-icon left>mdi-file-excel</v-icon>
            Descargar Excel
          </v-btn>
          <v-spacer></v-spacer>
          <div class="caption grey--text mb-2">
            {{ meta.totalCount }} periodos | {{ formatNumber(meta.totals.ticketsCount) }} tickets | {{ formatCurrency(meta.totals.totalSales) }}
          </div>
        </div>
      </v-card-text>
    </v-card>

    <v-card>
      <v-card-title>
        Vista previa
      </v-card-title>
      <v-data-table
        :headers="headers"
        :items="rows"
        :loading="loading"
        class="elevation-1"
        dense
        :footer-props="{ itemsPerPageText: 'Filas por pagina', itemsPerPageOptions: [10, 25, 50, 100] }"
      >
        <template #[`item.ticketsCount`]="{ item }">
          {{ formatNumber(item.ticketsCount) }}
        </template>
        <template #[`item.productsCount`]="{ item }">
          {{ formatNumber(item.productsCount) }}
        </template>
        <template #[`item.totalSales`]="{ item }">
          {{ formatCurrency(item.totalSales) }}
        </template>
        <template #[`item.sellerName`]="{ item }">
          {{ item.sellerName || 'Todos' }}
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import * as salesService from '@/services/salesService';

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const defaultFilters = () => {
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
  return {
    dateFrom: formatDate(firstDay),
    dateTo: formatDate(today),
    groupBy: 'day',
    sellerId: null,
  };
};

export default {
  name: 'SalesSummaryReportView',
  data() {
    return {
      loading: false,
      downloading: false,
      sellers: [],
      rows: [],
      meta: {
        totalCount: 0,
        totals: {
          ticketsCount: 0,
          totalSales: 0,
          productsCount: 0,
        },
      },
      filters: defaultFilters(),
      groupOptions: [
        { text: 'Dia', value: 'day' },
        { text: 'Semana', value: 'week' },
        { text: 'Mes', value: 'month' },
      ],
      headers: [
        { text: 'Periodo', value: 'periodLabel' },
        { text: 'Tickets', value: 'ticketsCount', align: 'end' },
        { text: 'Productos/Unidades', value: 'productsCount', align: 'end' },
        { text: 'Total vendido', value: 'totalSales', align: 'end' },
        { text: 'Vendedor', value: 'sellerName' },
      ],
    };
  },
  async mounted() {
    await this.loadSellers();
    await this.runReport();
  },
  methods: {
    cleanParams() {
      const params = { ...this.filters };
      Object.keys(params).forEach((key) => {
        if (params[key] === '' || params[key] === undefined || params[key] === null) {
          delete params[key];
        }
      });
      return params;
    },
    async loadSellers() {
      try {
        this.sellers = await salesService.fetchSalesSellers();
      } catch (error) {
        this.$emit('show-alert', { message: 'Error al cargar vendedores.', type: 'error' });
      }
    },
    async runReport() {
      if (!this.filters.dateFrom || !this.filters.dateTo) {
        this.$emit('show-alert', { message: 'Selecciona fecha inicial y final.', type: 'warning' });
        return;
      }

      this.loading = true;
      try {
        const response = await salesService.fetchSalesSummaryReportData(this.cleanParams());
        this.rows = response.rows || [];
        this.meta = response.meta || {
          totalCount: 0,
          totals: { ticketsCount: 0, totalSales: 0, productsCount: 0 },
        };
      } catch (error) {
        this.rows = [];
        this.meta = {
          totalCount: 0,
          totals: { ticketsCount: 0, totalSales: 0, productsCount: 0 },
        };
        this.$emit('show-alert', { message: 'Error al ejecutar el reporte de ventas.', type: 'error' });
      } finally {
        this.loading = false;
      }
    },
    clearFilters() {
      this.filters = defaultFilters();
      this.runReport();
    },
    async downloadReport() {
      this.downloading = true;
      try {
        const blob = await salesService.downloadSalesSummaryReport(this.cleanParams());
        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `reporte_ventas_resumen_${new Date().toISOString().slice(0, 10)}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        this.$emit('show-alert', { message: 'Error al descargar el reporte de ventas.', type: 'error' });
      } finally {
        this.downloading = false;
      }
    },
    formatCurrency(value) {
      return `$${Number(value || 0).toFixed(2)}`;
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString('es-MX');
    },
  },
};
</script>
